import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

type ShowFn = (message: string, onUpgrade: () => void) => void;

const Ctx = createContext<ShowFn>(() => {});

/** Show a centered, persistent plan-limit dialog with an Upgrade action. */
export const usePlanLimit = () => useContext(Ctx);

export function PlanLimitProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ message: string; onUpgrade: () => void } | null>(null);
  const show = useCallback<ShowFn>((message, onUpgrade) => setState({ message, onUpgrade }), []);
  const close = () => setState(null);

  return (
    <Ctx.Provider value={show}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-neutral-900">Plan limit reached</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{state.message}</p>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  state.onUpgrade();
                  close();
                }}
                className="flex-1 rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-neutral-700"
              >
                Upgrade plan
              </button>
              <button
                type="button"
                onClick={close}
                className="rounded-md border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
